import React, { useState } from 'react';

const ALLOWED_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const COLORS = {
  text: '#2b2e42',
  muted: '#6b6e85',
  border: '#cfd3e0',
  line: '#e3e6ee',
  warn: '#b00020',
  warnBg: '#fdecec',
  electric: '#1462D2',
  orange: '#FF5A28',
  recommendedBg: '#eef4ff',
  ok: '#2e7d32',
  okBg: '#e6f4e6',
};

export const pillStyle = (color, background) => ({
  display: 'inline-block',
  fontSize: '10.5px',
  fontWeight: 700,
  letterSpacing: '.4px',
  borderRadius: '10px',
  padding: '2px 9px',
  verticalAlign: 'middle',
  color,
  background,
});

const buttonStyle = (color = COLORS.text, borderColor = COLORS.border) => ({
  background: '#fff',
  border: `1px solid ${borderColor}`,
  color,
  borderRadius: '6px',
  padding: '5px 12px',
  fontSize: '12.5px',
  cursor: 'pointer',
});

const readAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const [type, content] = reader.result.split(';');
      resolve(`${type};name=${file.name};${content}`);
    };
    reader.onerror = reject;
  });

export const readUploadFile = async (file) => {
  if (!ALLOWED_TYPES.includes(file.type)) {
    return { error: 'Only PDF, JPG or PNG files are accepted.' };
  }
  if (file.size > MAX_FILE_BYTES) {
    return { error: 'File exceeds 10 MB. Please upload a smaller file.' };
  }
  return { value: await readAsBase64(file) };
};

const fileNameFromValue = (value) => {
  const namePart = typeof value === 'string' ? value.split(';').find((part) => part.startsWith('name=')) : '';
  return namePart ? decodeURI(namePart.replace('name=', '')) : '';
};

const UkDocumentUpload = ({ id, title, required, description, value, onChange, extraAction, hasError, errorMessage, highlight }) => {
  const [fileInfo, setFileInfo] = useState(null);
  const [fileError, setFileError] = useState('');

  const handleFile = async (file) => {
    setFileError('');
    if (!file) {
      return;
    }
    const { value: base64, error } = await readUploadFile(file);
    if (error) {
      setFileError(error);
      return;
    }
    onChange(base64);
    setFileInfo({ name: file.name, size: file.size });
  };

  const remove = () => {
    setFileInfo(null);
    setFileError('');
    onChange(null);
  };

  const uploaded = Boolean(value);
  const name = fileInfo?.name || fileNameFromValue(value);
  const size = fileInfo ? ` (${(fileInfo.size / 1024).toFixed(0)} KB)` : '';
  const error = fileError || (hasError ? errorMessage : '');

  return (
    <div
      style={{
        border: `1.5px ${uploaded || highlight ? 'solid' : 'dashed'} ${uploaded ? '#bfe0bf' : highlight ? COLORS.warn : COLORS.border}`,
        borderRadius: '8px',
        padding: '13px 15px',
        background: uploaded ? '#f6fbf6' : highlight ? COLORS.warnBg : '#fbfcfe',
      }}
    >
      <h4 style={{ margin: '0 0 3px', fontSize: '13px', color: COLORS.text, fontWeight: 600 }}>
        {title}{' '}
        {uploaded ? (
          <span style={pillStyle(COLORS.ok, COLORS.okBg)}>UPLOADED</span>
        ) : required ? (
          <span style={pillStyle(COLORS.warn, COLORS.warnBg)}>REQUIRED</span>
        ) : (
          <span style={pillStyle(COLORS.electric, COLORS.recommendedBg)}>RECOMMENDED (Optional)</span>
        )}
      </h4>
      <p style={{ margin: '0 0 9px', fontSize: '11.5px', color: COLORS.muted, lineHeight: 1.4 }}>{description}</p>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
        {extraAction}
        <label htmlFor={id} style={buttonStyle()}>
          {uploaded ? 'Replace file' : 'Upload file'}
        </label>
        <input
          id={id}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          style={{ display: 'none' }}
          onChange={(e) => {
            handleFile(e.target.files[0]);
            e.target.value = '';
          }}
        />
        {uploaded ? (
          <>
            <span style={{ fontSize: '12px', color: COLORS.text }}>{`${name}${size}`}</span>
            <button type="button" style={{ ...buttonStyle(COLORS.warn, 'transparent'), padding: 0 }} onClick={remove}>
              remove
            </button>
          </>
        ) : (
          <span style={{ fontSize: '11px', color: COLORS.muted }}>PDF, JPG or PNG · max 10 MB</span>
        )}
      </div>
      {error && <span style={{ display: 'block', marginTop: '4px', fontSize: '11px', color: COLORS.warn }}>{error}</span>}
    </div>
  );
};

export const documentButtonStyle = buttonStyle;
export const DOCUMENT_COLORS = COLORS;

export default UkDocumentUpload;
